import {
    prefillPhoneNumber,
    PrefillType,
} from '@getstep/sdk/dist/store/testing/prefill'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { useRef, useState } from 'react'

import { Button } from '@components/button/button'
import { TelephoneInput } from '@components/input/telephone-input'
import styles from '@components/playbook/phone-verification/phone-verification.module.scss'
import { TestFeatureFlagsModal } from '@components/testing/TestFeatureFlagsModal'
import analyticsService from '@lib/analytics/AnalyticsService'
import { useEffectAsync } from '@lib/hooks/useEffectAsync'
import { useVerificationFocus } from '@lib/hooks/useVerificationFocus'
import { useViewModel } from '@lib/hooks/useViewModel'
import { PhoneVerificationViewModel } from '@lib/store/playbook/PhoneVerificationViewModel'
import { isPrd } from '@shared/util/env'
import { testApiFactory } from '@shared/util/tests/factory'

export const PhoneVerification = observer(() => {
    const phoneInputRef = useRef<HTMLInputElement>(null)
    const codeInputRef = useRef<HTMLInputElement>(null)
    const [showPrefillDialog, setShowPrefillDialog] = useState(false)
    const [showFeatureFlagsDialog, setShowFeatureFlagsDialog] = useState(false)

    const viewModel = useViewModel(PhoneVerificationViewModel)

    useVerificationFocus(viewModel, phoneInputRef, codeInputRef)

    // Start session recording when component mounts
    useEffectAsync(async () => {
        await analyticsService.startSessionRecording()
    }, [])

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        viewModel.setInputValue(e.target.value)
    }

    const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        viewModel.setVerificationCode(e.target.value)
    }

    const handleConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        viewModel.setConsentChecked(e.target.checked)
    }

    const handlePrefillTypeSelect = (type: PrefillType) => {
        const phoneNumber = prefillPhoneNumber(type).substring(1)
        viewModel.setInputValue(phoneNumber)
        setShowPrefillDialog(false)
    }

    const prefillTypes = [
        { type: PrefillType.TRUST_SCORE_TOO_LOW, label: 'Trust Score Too Low' },
        { type: PrefillType.NO_PREFILL, label: 'No Prefill' },
        { type: PrefillType.DOB_EMAIL, label: 'DOB + Email' },
        { type: PrefillType.EMAIL_ONLY, label: 'Email Only' },
        {
            type: PrefillType.NO_DOB_SSN_OR_EMAIL,
            label: 'No SSN, DOB, or Email',
        },
        { type: PrefillType.DOB_ONLY, label: 'DOB Only' },
        {
            type: PrefillType.DOB_AND_SSN_WITH_VERIFIED_EMAIL,
            label: 'DOB + SSN (Verified Email)',
        },
        { type: PrefillType.PROVE, label: 'Prove' },
    ]

    return (
        <div className={styles.phoneVerification}>
            <div>
                {viewModel.currentStep === 'input' ? (
                    <>
                        <h1 className={styles.title}>
                            Sign up with your mobile phone number
                        </h1>

                        <div className={styles.inputContainer}>
                            <TelephoneInput
                                value={viewModel.inputValue}
                                onChange={handlePhoneChange}
                                className={classNames(
                                    styles.phoneInput,
                                    'mp-mask',
                                )}
                                disabled={viewModel.loading}
                                size='medium'
                                preset='material'
                                valid={!viewModel.hasError}
                                error={viewModel.errorMessage}
                                fullWidth
                                name='phoneNumber'
                                validationCount={viewModel.hasError ? 1 : 0}
                                autoComplete='mobile tel'
                                placeholder='(555) 555-5555'
                            />
                        </div>

                        <p className={styles.codeInfo}>
                            We’ll text you a 6-digit code
                        </p>

                        <div className={styles.footer}>
                            <div className={styles.checkboxContainer}>
                                <label className={styles.customCheckbox}>
                                    <input
                                        type='checkbox'
                                        checked={viewModel.isConsentChecked}
                                        onChange={handleConsentChange}
                                    />
                                    <span className={styles.checkmark} />
                                </label>
                                <span className={styles.termsText}>
                                    I consent to receive SMS marketing messages
                                    from Step. Message frequency varies and data
                                    rates apply. See{' '}
                                    <a
                                        href='/policies/mobileterms'
                                        className={styles.link}
                                        target='_blank'
                                    >
                                        Mobile Terms
                                    </a>{' '}
                                    for more information.
                                </span>
                            </div>

                            <Button
                                size='medium'
                                preset='futureFuscia'
                                className={styles.button}
                                onClick={() => viewModel.handleSendCode()}
                                disabled={!viewModel.canSubmitInput}
                                loading={viewModel.loading}
                            >
                                Send Code
                            </Button>
                        </div>

                        {!isPrd() && (
                            <>
                                <br />
                                <Button
                                    size='medium'
                                    preset='coalMineCanary'
                                    className={styles.button}
                                    onClick={async () =>
                                        viewModel.setInputValue(
                                            await testApiFactory.getUnlinkedPhoneNumber(),
                                        )
                                    }
                                >
                                    Test Phone Number
                                </Button>
                                <br />
                                <Button
                                    size='medium'
                                    preset='coalMineCanary'
                                    className={styles.button}
                                    onClick={() => setShowPrefillDialog(true)}
                                >
                                    Test Prefill Phone Number
                                </Button>
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <h1 className={styles.title}>
                            Let&apos;s make sure it&apos;s you
                        </h1>

                        <p className={styles.copy}>
                            Enter the 6-digit code we sent to
                            <br />
                            {viewModel.phoneNumberDisplay}
                        </p>

                        <div className={styles.inputContainer}>
                            <input
                                ref={codeInputRef}
                                type='text'
                                inputMode='numeric'
                                value={viewModel.verificationCode}
                                onChange={handleCodeChange}
                                onKeyPress={(e) => viewModel.handleKeyPress(e)}
                                placeholder='Enter Code'
                                className={classNames(
                                    styles.codeInput,
                                    'mp-mask',
                                )}
                                disabled={viewModel.loading}
                                maxLength={6}
                                id='phoneNumber'
                            />
                            {viewModel.hasError && (
                                <span className={styles.error}>
                                    {viewModel.errorMessage}
                                </span>
                            )}
                        </div>
                        <div style={{ marginBottom: '32px' }}>
                            <p className={styles.wrongNumber}>
                                Made a typo? &nbsp;
                                <button
                                    onClick={() => viewModel.handleBack()}
                                    disabled={viewModel.loading}
                                    className={styles.updatePhoneButton}
                                >
                                    Update phone number
                                </button>
                            </p>
                        </div>

                        <div className={styles.footer}>
                            <button
                                className={styles.resendButton}
                                onClick={() => viewModel.handleResendCode()}
                                disabled={
                                    viewModel.loading ||
                                    !viewModel.canResendCodeFromStore
                                }
                            >
                                Resend Code
                            </button>
                            <Button
                                size='medium'
                                preset='futureFuscia'
                                className={styles.button}
                                onClick={() => viewModel.handleVerifyCode()}
                                disabled={!viewModel.canSubmitCode}
                                loading={viewModel.isSubmitting}
                            >
                                Submit Code
                            </Button>
                        </div>

                        <div
                            style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '8px',
                                marginTop: '16px',
                            }}
                        >
                            {!isPrd() && (
                                <>
                                    <Button
                                        size='medium'
                                        preset='coalMineCanary'
                                        onClick={() =>
                                            viewModel.setVerificationCode(
                                                '123456',
                                            )
                                        }
                                    >
                                        Send Test Code
                                    </Button>
                                    <Button
                                        size='medium'
                                        preset='coalMineCanary'
                                        onClick={() =>
                                            setShowFeatureFlagsDialog(true)
                                        }
                                    >
                                        Test Features
                                    </Button>
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Prefill Type Selection Dialog */}
            {showPrefillDialog && !isPrd() && (
                <div
                    className={styles.dialogOverlay}
                    onClick={() => setShowPrefillDialog(false)}
                >
                    <div
                        className={styles.dialog}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 className={styles.dialogTitle}>
                            Select Prefill Type
                        </h2>
                        <div className={styles.dialogContent}>
                            {prefillTypes.map(({ type, label }) => (
                                <button
                                    key={type}
                                    className={styles.prefillButton}
                                    onClick={() =>
                                        handlePrefillTypeSelect(type)
                                    }
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        <button
                            className={styles.dialogCloseButton}
                            onClick={() => setShowPrefillDialog(false)}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Feature Flags Selection Dialog */}
            {!isPrd() && (
                <TestFeatureFlagsModal
                    isOpen={showFeatureFlagsDialog}
                    onClose={() => setShowFeatureFlagsDialog(false)}
                    onFlagsChange={(flags) =>
                        viewModel.setTestFeatureFlagsToEnable(flags)
                    }
                />
            )}
        </div>
    )
})

export default PhoneVerification
